import Icon from 'components/ui/Icon/Icon';

import { ReactComponent as Spinner } from '../../../assets/icons/spinner.svg';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTransactionContext } from 'context/hooks/useTransactionContext';
import { usePageContext } from 'context/hooks/usePageContext';
import { PAGE_SUB } from 'context/pageContext';
import useGetCardPaymentStatus, {
  CARD_PAYMENT_SUCCESS_EVENT,
  CardPaymentStatus,
} from 'hooks/useGetCardPaymentStatus';
import Button from 'components/ui/Button/Button';
import { PAYMENT_PROVIDER } from 'types/enums';

const SETTLE_LIMIT_MS = 2 * 60 * 1000;
// Paystack: check the charge at least 5 seconds after the cardholder closes the
// bank's page, or 5 minutes after it opened, whichever comes first.
export const CLOSE_SETTLE_MS = 5 * 1000;
export const NOT_COMPLETED_LIMIT_MS = 5 * 60 * 1000;

// What Paystack's charge check reads until the cardholder finishes on the bank's
// page: a FAILED status that is not the payment's outcome yet.
export const NOT_COMPLETED_MESSAGE = 'The transaction was not completed';

const isNotCompleted = ({ status, message }: CardPaymentStatus) =>
  status === 'FAILED' && message === NOT_COMPLETED_MESSAGE;

const BankAuth = ({ url }: { url: string }) => {
  const { ceviantTransactionId, provider } = useTransactionContext();
  const { changeSub } = usePageContext();
  const isPaystack = provider === PAYMENT_PROVIDER.PAYSTACK;

  const [isLoading, setIsLoading] = useState(false);
  // true from opening the bank's page until the status after it closes is known
  const [isAwaitingBank, setIsAwaitingBank] = useState(false);
  const [isPopupBlocked, setIsPopupBlocked] = useState(false);
  // The browser can report the page closed while it is still open (e.g. when
  // the bank's page cuts it off from this one), so a close is not the end.
  const [isBankPageClosed, setIsBankPageClosed] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const [isNotCompletedExpired, setIsNotCompletedExpired] = useState(false);
  const popupWindowRef = useRef<Window | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const getUrlMap: Record<string, string> = {
    [PAYMENT_PROVIDER.PAYLOFT]: '/payloft/payment/status',
    default: '/payments/status',
  };

  const holdFailure = useCallback(
    (payment: CardPaymentStatus) =>
      isPaystack &&
      (isAwaitingBank || (isNotCompleted(payment) && !isNotCompletedExpired)),
    [isPaystack, isAwaitingBank, isNotCompletedExpired],
  );

  const { error, triggerFetch, status, message } = useGetCardPaymentStatus(
    ceviantTransactionId,
    {
      shouldFetch: isLoading,
      retry: true,
      interval: 5000,
      holdFailure,
    },
    getUrlMap[provider as string] || getUrlMap.default,
  );

  const isWaitingOnCardholder =
    isPaystack && !isNotCompletedExpired && isNotCompleted({ status, message });

  // A failed poll must not close the bank's page mid-authorization.
  useEffect(() => {
    if (error && !isAwaitingBank) {
      changeSub(PAGE_SUB.ERROR);
    }
  }, [error, isAwaitingBank, changeSub]);

  // Once the bank's page closes, the next status is the payment's outcome.
  useEffect(() => {
    if (!isAwaitingBank) return;

    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const timer = setInterval(() => {
      if (popupWindowRef.current?.closed) {
        clearInterval(timer);
        setIsBankPageClosed(true);
        // The bank can take a few seconds to report back once its page closes.
        settleTimer = setTimeout(() => {
          Promise.resolve(triggerFetch()).finally(() =>
            setIsAwaitingBank(false),
          );
        }, CLOSE_SETTLE_MS);
      }
    }, 1000);

    return () => {
      clearInterval(timer);
      if (settleTimer) {
        clearTimeout(settleTimer);
      }
    };
  }, [isAwaitingBank, triggerFetch]);

  // Restarted on every opening of the bank's page.
  useEffect(() => {
    if (!openCount) return;

    setIsNotCompletedExpired(false);
    const timer = setTimeout(
      () => setIsNotCompletedExpired(true),
      NOT_COMPLETED_LIMIT_MS,
    );

    return () => clearTimeout(timer);
  }, [openCount]);

  // The bank's page is closed but the payment has not settled, so stop waiting.
  // A payment the cardholder has not finished is not pending: it ends when the
  // not-completed limit does.
  useEffect(() => {
    if (!isLoading || isAwaitingBank || isWaitingOnCardholder) return;

    const timer = setTimeout(
      () => changeSub(PAGE_SUB.PAYING),
      SETTLE_LIMIT_MS,
    );

    return () => clearTimeout(timer);
  }, [isLoading, isAwaitingBank, isWaitingOnCardholder, changeSub]);

  useEffect(() => {
    const closePopupNow = () => {
      if (popupWindowRef.current && !popupWindowRef.current.closed) {
        popupWindowRef.current.close();
      }
    };

    const closePopup = () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }

      closeTimerRef.current = setTimeout(() => {
        closePopupNow();
      }, 450);
    };

    window.addEventListener(CARD_PAYMENT_SUCCESS_EVENT, closePopup);

    return () => {
      window.removeEventListener(CARD_PAYMENT_SUCCESS_EVENT, closePopup);
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      closePopupNow();
    };
  }, []);

  const handleClick = () => {
    // Without a URL the popup would open blank and never finish.
    if (!url) {
      changeSub(
        PAGE_SUB.ERROR,
        "We could not open your bank's page. Please start your payment again.",
      );
      return;
    }

    // Some providers send the bank's page as HTML (e.g. an auto-submitting
    // form) rather than a URL, so that is written into a blank tab instead.
    const isHtml = !/^https?:\/\//i.test(url.trim());

    // Opened within the click so that popup blockers allow it. Without window
    // features the browser opens a tab in the same window, not a popup.
    const popup = window.open(isHtml ? '' : url, 'PopupSecureWindow');

    if (!popup) {
      setIsPopupBlocked(true);
      return;
    }

    if (isHtml) {
      popup.document.open();
      popup.document.write(url);
      popup.document.close();
    }

    popupWindowRef.current = popup;
    setIsPopupBlocked(false);
    setIsBankPageClosed(false);
    setOpenCount((count) => count + 1);
    setIsAwaitingBank(true);
    setIsLoading(true);
  };

  // The page closed and the cardholder has not finished, so let them go back.
  const canReturnToBank =
    isBankPageClosed && !isAwaitingBank && isWaitingOnCardholder;

  return (
    <div className="flex flex-col items-center gap-12 py-20">
      <div className="space-y-12 text-center ">
        <h3 className="uppercase text-xl md:text-2xl font-light tracking-wider text-Primary-200">
          CEVIANT AUTHORIZATION
        </h3>
        <div>
          {canReturnToBank ? (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-[#535353]">
                Your bank&apos;s page closed before the payment was finished. If
                it is still open, finish there. Otherwise, return to your bank to
                continue.
              </p>
              {isPopupBlocked && (
                <p className="text-red-500 text-sm">
                  Your browser blocked your bank&apos;s page. Allow popups for
                  this site and try again.
                </p>
              )}
              <Button
                type="button"
                onClick={handleClick}
              >
                Return to your bank
              </Button>
            </div>
          ) : isLoading ? (
            <div className="text-center flex flex-col items-center gap-4">
              <p className="font-semibold text-lg text-[#535353]">
                You are being redirected to your bank to protect your card
                against unauthorized use.
              </p>

              <Icon
                svg={Spinner}
                size={40}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {isPopupBlocked && (
                <p className="text-red-500 text-sm">
                  Your browser blocked your bank&apos;s page. Allow popups for
                  this site and try again.
                </p>
              )}
              <Button onClick={handleClick}>
                Click to continue to your bank
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BankAuth;
